import {
  formatFocalLengthRange,
  getLensDisplayName,
  getLensSeriesKey,
  getLensSeriesLabel,
} from "./lens";


const compareText = (left, right) =>
  (left || "").localeCompare(right || "", undefined, { sensitivity: "base" });


const compareLensByFocal = (left, right) => {
  const leftMin = Number.parseFloat(left?.focal_length_min_mm ?? left?.focal_length_mm);
  const rightMin = Number.parseFloat(right?.focal_length_min_mm ?? right?.focal_length_mm);
  const leftMax = Number.parseFloat(left?.focal_length_max_mm ?? left?.focal_length_mm);
  const rightMax = Number.parseFloat(right?.focal_length_max_mm ?? right?.focal_length_mm);

  if (Number.isFinite(leftMin) && Number.isFinite(rightMin) && leftMin !== rightMin) {
    return leftMin - rightMin;
  }

  if (Number.isFinite(leftMax) && Number.isFinite(rightMax) && leftMax !== rightMax) {
    return leftMax - rightMax;
  }

  return compareText(left?.id, right?.id);
};


export const buildLensSeriesGroups = (lenses = []) => {
  const seriesGroups = new Map();

  lenses.forEach((lens) => {
    if (!lens?.id) {
      return;
    }

    const seriesKey = getLensSeriesKey(lens);
    if (!seriesKey) {
      return;
    }

    if (!seriesGroups.has(seriesKey)) {
      seriesGroups.set(seriesKey, {
        key: seriesKey,
        brand: lens.brand || "",
        series: lens.series || "",
        label: getLensSeriesLabel(lens),
        lenses: [],
        lensIds: [],
      });
    }

    const seriesGroup = seriesGroups.get(seriesKey);
    const lensId = String(lens.id);

    if (!seriesGroup.lensIds.includes(lensId)) {
      seriesGroup.lensIds.push(lensId);
      seriesGroup.lenses.push(lens);
    }
  });

  return Array.from(seriesGroups.values())
    .map((seriesGroup) => ({
      ...seriesGroup,
      lenses: [...seriesGroup.lenses].sort(compareLensByFocal),
      lensIds: [...seriesGroup.lensIds].sort((left, right) => {
        const leftLens = seriesGroup.lenses.find((lens) => String(lens.id) === left);
        const rightLens = seriesGroup.lenses.find((lens) => String(lens.id) === right);
        return compareLensByFocal(leftLens, rightLens);
      }),
      focalLabels: [...seriesGroup.lenses]
        .sort(compareLensByFocal)
        .map((lens) =>
          formatFocalLengthRange(lens.focal_length_min_mm, lens.focal_length_max_mm),
        )
        .filter(Boolean),
    }))
    .sort((left, right) => compareText(left.label, right.label));
};


export const buildManufacturerTree = (products = []) => {
  const tree = {};

  products.forEach((product) => {
    const camera = product?.camera;
    const lens = product?.lens;

    if (!camera?.id) {
      return;
    }

    const manufacturerName = camera.brand || "Unknown manufacturer";

    if (!tree[manufacturerName]) {
      tree[manufacturerName] = {
        name: manufacturerName,
        cameras: {},
      };
    }

    if (!tree[manufacturerName].cameras[camera.id]) {
      tree[manufacturerName].cameras[camera.id] = {
        camera,
        lensBrands: {},
      };
    }

    if (!lens?.id) {
      return;
    }

    const seriesKey = getLensSeriesKey(lens);
    if (!seriesKey) {
      return;
    }

    const cameraEntry = tree[manufacturerName].cameras[camera.id];
    const lensBrandKey = (lens.brand || "").toLowerCase().trim() || "unknown";

    if (!cameraEntry.lensBrands[lensBrandKey]) {
      cameraEntry.lensBrands[lensBrandKey] = {
        brand: lens.brand || "",
        seriesGroups: {},
      };
    }

    const lensBrandEntry = cameraEntry.lensBrands[lensBrandKey];

    if (!lensBrandEntry.seriesGroups[seriesKey]) {
      lensBrandEntry.seriesGroups[seriesKey] = {
        key: seriesKey,
        brand: lens.brand || "",
        series: lens.series || "",
        label: getLensSeriesLabel(lens, { includeBrand: false }) || getLensSeriesLabel(lens),
        lenses: [],
        lensIds: [],
      };
    }

    const seriesGroup = lensBrandEntry.seriesGroups[seriesKey];
    const lensId = String(lens.id);

    if (!seriesGroup.lensIds.includes(lensId)) {
      seriesGroup.lensIds.push(lensId);
      seriesGroup.lenses.push(lens);
    }
  });

  return Object.values(tree)
    .map((manufacturer) => ({
      name: manufacturer.name,
      cameras: Object.values(manufacturer.cameras)
        .map((cameraEntry) => ({
          camera: cameraEntry.camera,
          lensBrands: Object.values(cameraEntry.lensBrands)
            .map((brandEntry) => ({
              brand: brandEntry.brand,
              seriesGroups: Object.values(brandEntry.seriesGroups)
                .map((seriesGroup) => ({
                  ...seriesGroup,
                  lenses: [...seriesGroup.lenses].sort(compareLensByFocal),
                  lensIds: [...seriesGroup.lensIds].sort((left, right) => {
                    const leftLens = seriesGroup.lenses.find((lens) => String(lens.id) === left);
                    const rightLens = seriesGroup.lenses.find((lens) => String(lens.id) === right);
                    return compareLensByFocal(leftLens, rightLens);
                  }),
                }))
                .sort((left, right) => compareText(left.label, right.label)),
            }))
            .sort((left, right) => compareText(left.brand, right.brand)),
        }))
        .sort((left, right) => compareText(left.camera?.model_name, right.camera?.model_name)),
    }))
    .sort((left, right) => compareText(left.name, right.name));
};


export const getFilterSnapshotManufacturers = (snapshot) => (
  Array.isArray(snapshot?.manufacturers) ? snapshot.manufacturers : []
);


export const normalizeLensSeriesKey = (value) => String(value || "").trim().toLowerCase();


export const getFallbackLensSeriesLabel = (seriesKey) => (
  normalizeLensSeriesKey(seriesKey).split("::").filter(Boolean).join(" ")
);


export const getFilterProductLabel = (product) => {
  if (!product) {
    return "";
  }

  if (product.label) {
    return product.label;
  }

  return [
    product.camera?.brand,
    product.camera?.model_name,
    getLensDisplayName(product.lens),
    product.serial_number ? `#${product.serial_number}` : "#n/a",
  ]
    .filter(Boolean)
    .join(" ");
};


export const buildSelectedLensSeriesGroups = (lenses = [], selectedLensSeriesKeys = []) => {
  const selectedSet = new Set(
    (selectedLensSeriesKeys || []).map((seriesKey) => normalizeLensSeriesKey(seriesKey)),
  );

  return buildLensSeriesGroups(lenses).filter((seriesGroup) => selectedSet.has(seriesGroup.key));
};


export const buildFilterSnapshotIndexes = (manufacturers = []) => {
  const cameraById = new Map();
  const lensSeriesByKey = new Map();

  manufacturers.forEach((manufacturer) => {
    (manufacturer.cameras || []).forEach(({ camera, lensBrands }) => {
      if (camera?.id) {
        cameraById.set(String(camera.id), camera);
      }

      (lensBrands || []).forEach(({ seriesGroups }) => {
        (seriesGroups || []).forEach((lensSeries) => {
          if (lensSeries?.key) {
            lensSeriesByKey.set(normalizeLensSeriesKey(lensSeries.key), lensSeries);
          }
        });
      });
    });
  });

  return {
    cameraById,
    lensSeriesByKey,
  };
};


export const buildSelectedLibraryFilters = ({
  manufacturers = [],
  cameraIds = [],
  lensSeriesKeys = [],
}) => {
  const { cameraById, lensSeriesByKey } = buildFilterSnapshotIndexes(manufacturers);

  const selectedCameras = Array.from(new Set(cameraIds.map((id) => String(id).trim()).filter(Boolean)))
    .map((cameraId) => (
      cameraById.get(cameraId)
      || {
        id: cameraId,
        brand: "",
        model_name: `Camera #${cameraId}`,
        label: `Camera #${cameraId}`,
      }
    ));

  const selectedLensSeries = Array.from(
    new Set(lensSeriesKeys.map((seriesKey) => normalizeLensSeriesKey(seriesKey)).filter(Boolean))
  ).map((seriesKey) => (
    lensSeriesByKey.get(seriesKey)
    || {
      key: seriesKey,
      label: getFallbackLensSeriesLabel(seriesKey),
      lenses: [],
    }
  ));

  return {
    cameras: selectedCameras,
    lensSeries: selectedLensSeries,
  };
};
